/**
 * @description Modal for adding a new card via HPF (Hosted Payment Fields).
 * PCI-compliant: card data goes directly to Elavon, never our servers.
 *
 * Key features:
 * - Uses existing HostedPaymentFields component for card capture
 * - Creates payment session → captures card via HPF → vaults card
 * - Refreshes card list on success
 *
 * @dependencies
 * - HostedPaymentFields: Elavon HPF iframe component
 * - storedCardsService: API client for session + vault operations
 *
 * @notes
 * - Nominal $1.00 amount is passed for HPF init — EPG requires a non-zero Order
 *   amount, but no charge occurs because the session uses doCreateTransaction=false.
 * - The HPF session is created inside useHostedPaymentFields (not here) — this
 *   modal only fetches the public clientConfig up front for SDK initialization.
 */

import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AlertCircle } from 'lucide-react';
import storedCardsService from '../../services/api/storedCardsService';
import HostedPaymentFields from '../shared/HostedPaymentFields';

export default function AddCardModal({ customerId, customerData, onClose, onCardAdded }) {
  const [clientConfig, setClientConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vaulting, setVaulting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load HPF client config on mount. The HPF session itself is created inside
  // useHostedPaymentFields, so we don't pre-create one here.
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = await storedCardsService.getClientConfig();

        if (!cancelled) {
          setClientConfig(config);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to initialize card capture. Please try again.');
          console.error('AddCardModal init error:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  // Handle HPF card capture callback
  const handleHpfSubmit = async (cardData) => {
    if (!cardData?.hostedCardId || !cardData?.sessionId) return;

    try {
      setVaulting(true);
      setError(null);

      await storedCardsService.vaultCard(cardData.sessionId, {
        hostedCardId: cardData.hostedCardId,
        customerId,
        customerData: {
          fullName: customerData
            ? `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim()
            : undefined,
          email: customerData?.email
        }
      });

      setSuccess(true);
      setTimeout(() => {
        onCardAdded();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save card. Please try again.');
      console.error('Card vault error:', err);
    } finally {
      setVaulting(false);
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">Add Payment Method</Dialog.Title>
          <button
            onClick={onClose}
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-900">Card added successfully</p>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4" />
              <p className="text-gray-500">Setting up secure card entry...</p>
            </div>
          ) : error && !clientConfig ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-600">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <p className="text-sm text-gray-500 mb-4">
                Enter card details below. Card data is processed securely and never stored on our servers.
              </p>

              {clientConfig && (
                <HostedPaymentFields
                  amount={100}
                  currency="USD"
                  description="Card on file setup"
                  clientConfig={clientConfig}
                  onSubmit={handleHpfSubmit}
                  submitButtonText={vaulting ? 'Saving...' : 'Save Card'}
                  processing={vaulting}
                />
              )}
            </>
          )}
        </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
